// src/services/api/categories.ts
import { httpClient } from '@/services/http/client';
import { ApiResponse, FrontendPaginatedResponse, PaginatedResponse } from '@/services/types/api';
import { Category, CategoryDetailResponse } from '@/services/types/domain';

class CategoriesApi {
    private readonly basePath = '/categories';

    /**
     * Obtener todas las categorías
     */
    async getCategories(filters: any): Promise<FrontendPaginatedResponse> {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined) {
                params.append(key, value ? value.toString() : '');
            }
        });

        const queryString = params.toString();
        const url = `admin${this.basePath}/${queryString ? `?${queryString}` : ''}`;

        const response = await httpClient.get<PaginatedResponse<any>>(url);
        //return response.data.data;
        return this.adaptBackendResponse(response.data);
    }
    async getCategoriesForSeller(filters: any): Promise<Category[]> {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined) {
                params.append(key, value ? value.toString() : '');
            }
        });

        const queryString = params.toString();
        const url = `${this.basePath}/${queryString ? `?${queryString}` : ''}`;

        const response = await httpClient.get<ApiResponse<Category[]>>(url);
        return response.data.data;
    }
    /**
     * Obtener categorías populares
     */
    async getPopularCategories(limit: number = 10): Promise<Category[]> {
        const response = await httpClient.get<ApiResponse<Category[]>>(
            `${this.basePath}/popular`,
            { params: { limit } }
        );
        return response.data.data;
    }

    /**
     * Obtener categoría por ID con productos y estadísticas
     */
    async getCategoryById(id: number): Promise<CategoryDetailResponse> {
        const response = await httpClient.get<ApiResponse<CategoryDetailResponse>>(
            `${this.basePath}/${id}`
        );
        return response.data.data;
    }

    /**
     * Obtener categoría por slug
     */
    async getCategoryBySlug(slug: string): Promise<Category> {
        const response = await httpClient.get<ApiResponse<Category>>(
            `${this.basePath}/slug/${slug}`
        );
        return response.data.data;
    }

    /**
     * Buscar categorías
     */
    async searchCategories(query: string): Promise<Category[]> {
        const response = await httpClient.get<ApiResponse<Category[]>>(
            `${this.basePath}/search`,
            { params: { q: query } }
        );
        return response.data.data;
    }

    /**
     * Crear nueva categoría (solo admin)
     */
    async createCategory(data: {
        name: string;
        description?: string;
        parent_id?: number | null;
        image?: string;
        icon?: string;
        sort_order?: number;
        is_active?: boolean;
        meta_title?: string;
        meta_description?: string;
    }): Promise<Category> {
        const response = await httpClient.post<ApiResponse<Category>>(
            this.basePath,
            data
        );
        return response.data.data;
    }
    /**
     * Desactivar una categoria
     */
    async desactivateCategory(id: number): Promise<Category> {
        const response = await httpClient.put<ApiResponse<Category>>(
            `${this.basePath}/${id}/inactive`
        );
        return response.data.data;
    }
    /**
     * Actualizar categoría (solo admin)
     */
    async updateCategory(id: number, data: Partial<{
        name: string;
        description?: string;
        parent_id?: number;
        image?: string;
        icon?: string;
        sort_order?: number;
        is_active?: boolean;
        meta_title?: string;
        meta_description?: string;
    }>): Promise<Category> {
        const response = await httpClient.put<ApiResponse<Category>>(
            `${this.basePath}/${id}`,
            data
        );
        return response.data.data;
    }

    /**
     * Eliminar categoría (solo admin)
     */
    async deleteCategory(id: number): Promise<void> {
        await httpClient.delete(`${this.basePath}/${id}`);
    }

    /**
     * Subir imagen de categoría (solo admin)
     */
    async uploadCategoryImage(id: number, image: File): Promise<{ image_url: string }> {
        const formData = new FormData();
        formData.append('image', image);

        const response = await httpClient.upload<ApiResponse<{ image_url: string }>>(
            `admin${this.basePath}/${id}/image`,
            formData
        );

        return response.data.data;
    }
    async uploadCategoryImage2(categoryId: number, file: File): Promise<{ image_url: string }> {
        const formData = new FormData();
        // OJO: el backend espera el campo "imagen"
        formData.append("imagen", file);

        const res = await httpClient.upload<ApiResponse<{ image_url: string }>>(
            `/categories/${categoryId}/image`,
            formData
        );
        return res.data.data; // { image_url }
    }
    private adaptBackendResponse<T>(backendResponse: PaginatedResponse<T>): FrontendPaginatedResponse<T> {
        const { data, pagination } = backendResponse;

        return {
            data,
            meta: {
                current_page: pagination.current_page,
                from: (pagination.current_page - 1) * pagination.per_page + 1,
                last_page: pagination.total_pages,
                per_page: pagination.per_page,
                to: Math.min(pagination.current_page * pagination.per_page, pagination.total),
                total: pagination.total,
            },
            links: {
                first: `?page=1`,
                last: `?page=${pagination.total_pages}`,
                prev: pagination.current_page > 1 ? `?page=${pagination.current_page - 1}` : undefined,
                next: pagination.has_more ? `?page=${pagination.current_page + 1}` : undefined,
            },
        };
    }
}

// Instancia singleton
export const categoriesApi = new CategoriesApi();
